/**
 * A one-subscriber bus letting anything ask the header's BugReportButton to
 * open its form.
 *
 * ⚠️ Why a bus rather than the obvious "put a second BugReportButton in the
 * modal": a second instance would carry its OWN snapshot + description state,
 * so which one the operator typed into would decide what got submitted. One
 * owner of the state, many triggers.
 *
 * The subscriber is the single `BugReportButton` mounted in the app shell, so
 * there is at most one at a time — hence a bare slot rather than a listener
 * set. A second subscriber replacing the first is a mounting bug, and the
 * console warning below names it rather than silently dropping one.
 */

type Listener = () => void;

let listener: Listener | null = null;

/** Register the handler that opens the bug-report form. Returns an unsubscribe. */
export function onBugReportRequested(fn: Listener): () => void {
  if (listener) {
    console.warn(
      '[bug-report] a second BugReportButton subscribed — the first is being ' +
      'replaced, so its state is now unreachable. Exactly one should be mounted.',
    );
  }
  listener = fn;
  return () => { if (listener === fn) listener = null; };
}

/**
 * Ask the header button to open its form.
 *
 * A no-op when nothing is subscribed — the button is gated on `report_bug`, so
 * a caller without that grant has no subscriber and must not throw. Callers
 * gate on the same grant before rendering a trigger.
 */
export function openBugReport(): void {
  listener?.();
}
